import { ref } from 'vue'
import { useRuntimeConfig } from '#app'
import { createLessonService } from '~/services/lesson.service'
import { COURSE_CONFIG } from '~/config/course'
import type { Lesson } from '~/types/lesson.types'

export function useLesson() {
  const config = useRuntimeConfig()
  const { locale } = useI18n()
  const service = createLessonService(config.public.landingApi)

  const courseSlug = computed(() => {
    const localeKey = locale.value as 'uk' | 'ru'
    return config.public.courseSlug || COURSE_CONFIG[localeKey]?.slug || COURSE_CONFIG.uk.slug
  })

  const lessons = ref<Lesson[]>([])
  const lesson = ref<Lesson | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  async function fetchLessons() {
    loading.value = true
    error.value = null
    try {
      lessons.value = await service.getLessons(courseSlug.value)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load lessons'
    } finally {
      loading.value = false
    }
  }

  async function fetchLesson(slug: string) {
    loading.value = true
    error.value = null
    try {
      const meta = await service.getLessonBySlug(courseSlug.value, slug)
      if (!meta) {
        lesson.value = null
        return
      }
      lesson.value = await service.getLessonById(meta._id)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load lesson'
    } finally {
      loading.value = false
    }
  }

  return { lessons, lesson, loading, error, fetchLessons, fetchLesson }
}
